<template>
  <!-- Hero Section -->
  <div class="relative min-h-screen bg-white overflow-hidden">
    <!-- Grid Pattern Background -->
    <div class="absolute inset-0 bg-grid-pattern opacity-20"></div>
    
    <!-- Subtle Gradient Overlay -->
    <div class="absolute inset-0 bg-gradient-to-br from-gray-50 to-white"></div>
    
    <!-- Floating Contact Icons -->
    <div class="absolute inset-0 pointer-events-none">
      <!-- Phone Icon -->
      <div class="absolute top-20 left-8 animate-float-slow opacity-30">
        <i class="fas fa-phone text-5xl text-blue-500"></i>
      </div>
      
      <!-- Email Icon -->
      <div class="absolute top-32 right-20 animate-float-medium opacity-25">
        <i class="fas fa-envelope text-6xl text-purple-500"></i>
      </div>
      
      <!-- Location Icon -->
      <div class="absolute top-1/2 left-24 animate-float-fast opacity-35">
        <i class="fas fa-map-marker-alt text-7xl text-red-500"></i>
      </div>
      
      <!-- Message Icon -->
      <div class="absolute top-3/4 right-32 animate-float-slow opacity-28">
        <i class="fas fa-comment text-4xl text-green-500"></i>
      </div>
      
      <!-- Contact Icon -->
      <div class="absolute bottom-32 left-16 animate-float-medium opacity-32">
        <i class="fas fa-address-book text-5xl text-orange-500"></i>
      </div>
      
      <!-- Chat Icon -->
      <div class="absolute top-1/3 left-1/3 animate-float-fast opacity-20">
        <i class="fas fa-comments text-6xl text-pink-500"></i>
      </div>
      
      <!-- Globe Icon -->
      <div class="absolute bottom-1/4 right-16 animate-float-slow opacity-30">
        <i class="fas fa-globe text-5xl text-teal-500"></i>
      </div>
      
      <!-- Headset Icon -->
      <div class="absolute top-1/4 right-1/3 animate-float-medium opacity-25">
        <i class="fas fa-headset text-4xl text-indigo-500"></i>
      </div>
      
      <!-- Paper Plane Icon -->
      <div class="absolute bottom-1/3 left-1/4 animate-float-fast opacity-35">
        <i class="fas fa-paper-plane text-5xl text-yellow-500"></i>
      </div>
      
      <!-- User Icon -->
      <div class="absolute top-2/3 right-1/4 animate-float-slow opacity-28">
        <i class="fas fa-user text-6xl text-cyan-500"></i>
      </div>
    </div>
    
    <!-- Hero Content -->
    <div class="relative z-10 flex items-center justify-center min-h-screen px-6 lg:px-16">
      <div class="max-w-6xl mx-auto mt-32">
        <div class="text-center mb-16">
          <!-- Main Heading -->
          <h1 class="text-6xl md:text-8xl font-light text-gray-900 mb-6 tracking-tight">
            Let's <span class="italic font-thin">Talk</span>
          </h1>
          
          <!-- Subtitle -->
          <p class="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Have a project in mind? We're here to turn your ideas into reality. 
            Drop us a message and let's start building something extraordinary.
          </p>
        </div>
        
        <!-- CTA Button -->
        <div class="text-center mb-20">
          <button @click="scrollToContact" 
                  class="group relative inline-flex items-center justify-center px-12 py-4 text-lg font-medium text-white bg-black rounded-none hover:bg-gray-800 transition-all duration-300 transform hover:scale-105">
            <span class="relative z-10">Get In Touch</span>
            <div class="absolute inset-0 border-2 border-black transform translate-x-2 translate-y-2 group-hover:translate-x-1 group-hover:translate-y-1 transition-transform duration-300"></div>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Contact Section -->
  <div class="relative min-h-screen flex items-center justify-center overflow-hidden bg-white">
    <!-- Background Curve -->
    <div class="absolute inset-0 bg-gray-900 clip-curve z-0"></div>
     
    <!-- Content Section -->
    <div class="relative z-10 flex flex-col md:flex-row items-start gap-6 px-6 lg:px-16 py-16 text-white w-full max-w-7xl">
      <!-- Contact Info -->
      <div class="md:w-1/2">
        <h2 class="text-3xl font-bold mb-4">CONTACT US</h2>
        <p class="mb-6">Stay updated on our activities!</p>
        <div class="space-y-6">
          <!-- Contact Icons/Info -->
          <div class="flex items-center space-x-4">
            <i class="fas fa-phone text-lg text-purple-400 bg-white rounded-full w-8 h-8 flex items-center justify-center"></i>
            <div>
              <p class="font-bold">CALL US</p>
              <p class="text-sm">+257 712 123 123</p>
            </div>
          </div>
          <div class="flex items-center space-x-4">
            <i class="fab fa-instagram text-lg text-pink-400 bg-white rounded-full w-8 h-8 flex items-center justify-center"></i>
            <div>
              <p class="font-bold">INSTAGRAM</p>
              <p class="text-sm">@mybusiness123</p>
            </div>
          </div>
          <div class="flex items-center space-x-4">
            <i class="fab fa-facebook-f text-lg text-blue-500 bg-white rounded-full w-8 h-8 flex items-center justify-center"></i>
            <div>
              <p class="font-bold">FACEBOOK</p>
              <p class="text-sm">My Business Page</p>
            </div>
          </div>
          <div class="flex items-center space-x-4">
            <i class="fas fa-map-marker-alt text-lg text-red-400 bg-white rounded-full w-8 h-8 flex items-center justify-center"></i>
            <div>
              <p class="font-bold">LOCATION</p>
              <p class="text-sm">The Mshel Hub, No. <br> 2 Julius Adelusi Street, Guzape, Abuja.</p>
            </div>
          </div>
        </div>
      </div>
       
      <!-- Contact Form -->
      <div class="md:w-1/2 bg-white p-6 rounded-lg shadow-lg text-black max-w-lg w-full mt-10 md:mt-0">
        <h3 class="text-2xl font-bold mb-6 text-center">Send Us a Message</h3>
        <form @submit.prevent="submitForm" class="space-y-4">
          <input v-model="form.name" type="text" placeholder="Your Name"
            class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black" required />
          <input v-model="form.email" type="email" placeholder="Your Email"
            class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black" required />
          <textarea v-model="form.message" rows="4" placeholder="Your Message"
            class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black" required></textarea>
          <button type="submit"
            class="w-full bg-black hover:bg-yellow text-white font-semibold py-2 px-4 rounded-md transition duration-200">
            Submit
          </button>
        </form>
      </div>
    </div>
  </div>
   
  <!-- Other section -->
  <FaQuestions />
</template>

<script>
import FaQuestions from '@/components/FaQuestions.vue';

export default {
  components: {
    FaQuestions,
  },
  data() {
    return {
      form: {
        name: "",
        email: "",
        message: "",
      },
    };
  },
  methods: {
    submitForm() {
      console.log("Form submitted:", this.form);
      alert("Thank you for contacting us!");
      this.form.name = "";
      this.form.email = "";
      this.form.message = "";
    },
    scrollToContact() {
      // Smooth scroll to contact section
      const contactSection = document.querySelector('.clip-curve').parentElement;
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  },
};
</script>

<style scoped>
.clip-curve {
  clip-path: ellipse(70% 100% at 0% 50%);
}

.bg-grid-pattern {
  background-image: 
    linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
}

/* Floating animations */
@keyframes float-slow {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
}

@keyframes float-medium {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(-3deg); }
}

@keyframes float-fast {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(2deg); }
}

.animate-float-slow {
  animation: float-slow 6s ease-in-out infinite;
}

.animate-float-medium {
  animation: float-medium 4s ease-in-out infinite;
}

.animate-float-fast {
  animation: float-fast 3s ease-in-out infinite;
}
</style>